import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Check, ChevronRight, X, Rocket, Database,
  Building2, FileText, PiggyBank, Users, Sparkles,
  type LucideProps,
} from 'lucide-react';
import { ComponentType } from 'react';
import { Card } from './ui/Card';
import { api } from '../lib/api';
import { useVendors } from '../hooks/useVendors';
import { useApInvoices } from '../hooks/useApInvoices';
import { useBudgetUtilization } from '../hooks/useBudgets';
import { useCan } from '../hooks/useCan';
import { useLoadSampleData } from '../hooks/useSampleData';
import { useToast } from './ui/Toast';

interface AgentRunLite {
  id: string;
}
interface MemberLite {
  id: string;
}

/**
 * First-run setup checklist (Wave 5). A small, dismissible HOME card listing a
 * few high-value setup steps with LIVE done/undone status derived from real
 * data (a vendor exists, an AP invoice exists, a budget is set, a teammate was
 * invited, an AI agent was run). Each row deep-links to the relevant page;
 * a step auto-checks the moment its underlying data exists.
 *
 * It hides itself once every step is complete OR the user dismisses it
 * (persisted in localStorage). A "Load sample data" shortcut lets a fresh
 * buyer satisfy several steps at once.
 */
export function SetupChecklist({ wsId }: { wsId: string | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canMutate } = useCan();
  const toast = useToast();
  const load = useLoadSampleData(wsId);

  const lsKey = `plyez.setup.dismissed.${wsId ?? ''}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !wsId) return false;
    return window.localStorage.getItem(lsKey) === '1';
  });

  const fy = new Date().getUTCFullYear();
  const vendors = useVendors(wsId);
  const apInvoices = useApInvoices(wsId);
  const budgets = useBudgetUtilization(wsId, fy);
  const members = useQuery<MemberLite[]>({
    queryKey: ['ws-members', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<MemberLite[]>(`/workspaces/${wsId}/members`)).data,
  });
  const agentRuns = useQuery<AgentRunLite[]>({
    queryKey: ['agent-runs', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<AgentRunLite[]>(`/workspaces/${wsId}/agents/runs`)).data,
  });

  const steps = useMemo<SetupStep[]>(
    () => [
      {
        id: 'vendor',
        icon: Building2,
        label: t('setup.steps.vendor'),
        done: (vendors.data?.length ?? 0) > 0,
        path: 'vendors',
      },
      {
        id: 'apInvoice',
        icon: FileText,
        label: t('setup.steps.apInvoice'),
        done: (apInvoices.data?.length ?? 0) > 0,
        path: 'ap-invoices',
      },
      {
        id: 'budget',
        icon: PiggyBank,
        label: t('setup.steps.budget'),
        done: (budgets.data?.length ?? 0) > 0,
        path: 'budgets',
      },
      {
        id: 'teammate',
        icon: Users,
        label: t('setup.steps.teammate'),
        // A solo workspace has exactly the owner; >1 member means someone was invited.
        done: (members.data?.length ?? 0) > 1,
        path: 'settings',
      },
      {
        id: 'agent',
        icon: Sparkles,
        label: t('setup.steps.agent'),
        done: (agentRuns.data?.length ?? 0) > 0,
        path: 'agents',
      },
    ],
    [t, vendors.data, apInvoices.data, budgets.data, members.data, agentRuns.data],
  );

  // Wait until every underlying query has resolved before deciding the card is
  // "all done" — otherwise we'd flash-hide it on a slow load.
  const loaded =
    !vendors.isLoading &&
    !apInvoices.isLoading &&
    !budgets.isLoading &&
    !members.isLoading &&
    !agentRuns.isLoading;
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = loaded && doneCount === steps.length;

  if (!wsId || dismissed || allDone) return null;

  const dismiss = () => {
    setDismissed(true);
    if (typeof window !== 'undefined') window.localStorage.setItem(lsKey, '1');
  };

  const onLoadSample = () => {
    load.mutate(undefined, {
      onSuccess: () => toast.success(t('sampleData.loaded')),
      onError: () => toast.error(t('sampleData.error')),
    });
  };

  return (
    <Card className="mb-7 overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-ink-200 dark:border-ink-800">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-9 w-9 shrink-0 rounded-lg bg-accent-50 dark:bg-accent-900/30 flex items-center justify-center">
            <Rocket size={17} className="text-accent-600 dark:text-accent-300" />
          </div>
          <div className="min-w-0">
            <div className="display font-semibold text-ink-900 dark:text-ink-100">
              {t('setup.title')}
            </div>
            <p className="text-xs text-ink-500 mt-0.5">
              {t('setup.progress', { done: doneCount, total: steps.length })}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('setup.dismiss')}
          className="shrink-0 rounded-md p-1 text-ink-400 hover:text-ink-700 dark:hover:text-ink-200"
        >
          <X size={15} />
        </button>
      </div>

      <ul className="divide-y divide-ink-100 dark:divide-ink-800">
        {steps.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => navigate(`/app/workspace/${wsId}/${s.path}`)}
              className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-ink-50 dark:hover:bg-ink-900 transition-colors"
            >
              <span
                className={
                  'h-6 w-6 shrink-0 rounded-full flex items-center justify-center ' +
                  (s.done
                    ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300'
                    : 'bg-ink-100 dark:bg-ink-800 text-ink-400')
                }
              >
                {s.done ? <Check size={14} /> : <s.icon size={13} />}
              </span>
              <span
                className={
                  'flex-1 text-sm ' +
                  (s.done
                    ? 'text-ink-400 line-through'
                    : 'text-ink-800 dark:text-ink-100 font-medium')
                }
              >
                {s.label}
              </span>
              {!s.done && <ChevronRight size={15} className="text-ink-300 dark:text-ink-600" />}
            </button>
          </li>
        ))}
      </ul>

      {canMutate && (
        <div className="px-5 py-3 border-t border-ink-200 dark:border-ink-800 bg-ink-50/50 dark:bg-ink-900/40">
          <button
            type="button"
            onClick={onLoadSample}
            disabled={load.isPending}
            className="inline-flex items-center gap-2 text-xs font-medium text-accent-700 dark:text-accent-300 hover:underline disabled:opacity-60"
          >
            <Database size={13} />
            {load.isPending ? t('sampleData.loading') : t('setup.loadSampleShortcut')}
          </button>
        </div>
      )}
    </Card>
  );
}

interface SetupStep {
  id: string;
  icon: ComponentType<LucideProps>;
  label: string;
  done: boolean;
  path: string;
}
